import { useMemo } from 'react';

import type {
  EditorContext,
  EditorContextElements,
  EditorContextHandlers,
  EditorContextUIHandlers,
} from 'flow-builder/types/context';

import { useContextMenu } from '../useContextMenu/useContextMenu';
import { useTooltip } from '../useTooltip/useTooltip';
import { useZoom } from '../useZoom/useZoom';

type UseUIHandlersOptions = Pick<EditorContext, 'props' | 'state' | 'view'> & {
  handlers: Pick<EditorContextHandlers, 'node'>;
};

type UseUIHandlersReturn = {
  handlers: EditorContextUIHandlers;
  elements: EditorContextElements;
};

export function useUIHandlers(opts: UseUIHandlersOptions): UseUIHandlersReturn {
  const { props, state, view } = opts;

  const { onContextMenuShow, onContextMenuHide, contextMenu } = useContextMenu({
    props,
    state,
    handlers: opts.handlers,
  });

  const { onTooltipShow, onTooltipHide, tooltip } = useTooltip();
  const onZoom = useZoom({ state, view });

  const handlers = useMemo<EditorContextUIHandlers>(
    () => ({
      onContextMenuShow,
      onContextMenuHide,
      onTooltipShow,
      onTooltipHide,
      onZoom,
    }),
    [
      onContextMenuShow,
      onContextMenuHide,
      onTooltipShow,
      onTooltipHide,
      onZoom,
    ],
  );

  const elements = useMemo<EditorContextElements>(
    () => ({
      contextMenu,
      tooltip,
    }),
    [contextMenu, tooltip],
  );

  return {
    handlers,
    elements,
  };
}
